import React, {
  ReactElement,
  FC,
  ReactNode,
  useState,
  useRef,
  useCallback,
  useMemo,
  Children,
  cloneElement,
  useEffect,
  useLayoutEffect,
} from "react";

import classNames from "classnames";

import { ItemProps } from "./Item/Item";
import { StyledNavListItemMore } from "./Item/Styles";
import { StyledNav, StyledNavAsSelect, StyledNavList } from "./Styles";
import { useResizeObserver } from "../../utils";
import { ExpandSingle } from "../Expanders/ExpandSingle";
import { Select } from "../Select/Select";

type NavType = "inline" | "dropdown";
export type NavAlignType = "left" | "center" | "right";
export type NavRoleType = "bold" | "light";

export interface INavProps {
  children?: ReactElement<ItemProps>[];
  /** Function for changing active navs. */
  onSelect?: (index: number) => void;
  /** Aligment of nav component. */
  alignment?: NavAlignType;
  /** CSS classes. */
  className?: string;
  /** Role of nav component. */
  role?: NavRoleType;
  /** Target id */
  targetId?: string;
}

export const Nav: FC<INavProps> = ({
  children,
  alignment = "left",
  onSelect,
  className,
  role = "bold",
  targetId,
}) => {
  const [active, setActive] = useState(0);
  const [moreOpened, setMoreOpened] = useState(false);
  const [type, setType] = useState<NavType>("inline");

  const setMoreOpen = useCallback(() => setMoreOpened(true), []);
  const setMoreClose = useCallback(() => setMoreOpened(false), []);

  const navItemNames = useMemo(() => {
    return children
      ? (Children.toArray(children) as ReactElement<ItemProps>[]).map(
          (item) => item?.props?.name
        )
      : [];
  }, [children]);

  const allOptions = useMemo(() => {
    return navItemNames?.map((name, index) => {
      return { id: index, name: name };
    });
  }, [navItemNames]);

  const navRef = useRef<HTMLUListElement | null>(null);
  const wrapRef = useRef<HTMLElement | null>(null);
  const dimensions = useResizeObserver(wrapRef);
  const childDimensions = useResizeObserver(navRef);

  const parentWidth = dimensions?.width ?? 0;
  const childWidth = childDimensions?.width ?? 0;

  const [childrenWidth, setChildrenWidth] = useState(childWidth);

  useLayoutEffect(() => {
    if (type === "inline") setChildrenWidth(childWidth);
  }, [childWidth, type]);

  const onClick = useCallback(
    (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
      if (e.metaKey || e.ctrlKey) {
        return;
      }
      if (
        e.target &&
        e.currentTarget.dataset.index &&
        !e.currentTarget.dataset.disabled
      ) {
        const index = Number(e.currentTarget.dataset.index);
        setActive(index);
        if (onSelect) {
          onSelect(index);
        }
      }
    },
    [onSelect]
  );

  const onMoreOptionClick = useCallback(
    (id) => {
      setActive(id);
      if (onSelect) {
        onSelect(id);
      }
    },
    [onSelect]
  );

  useMemo(() => {
    Children.forEach(
      children as ReactElement[],
      (child: ReactElement, index) => {
        if (child && child.props.active) {
          setActive(index);
        }
      }
    );
  }, [children]);

  const navAsSelect = useMemo(() => {
    return (
      <StyledNavAsSelect id={targetId} $active>
        <span>{navItemNames[active]}</span>
        <ExpandSingle expanded={moreOpened} fill="currentColor" />
      </StyledNavAsSelect>
    );
  }, [active, moreOpened, navItemNames, targetId]);

  useEffect(() => {
    if (!navRef.current || !wrapRef.current) return;
    if (!childrenWidth || !parentWidth) return;

    if (childrenWidth < parentWidth) {
      setType("inline");
    } else {
      setType("dropdown");
    }
  }, [childrenWidth, parentWidth]);

  if (!children) return;

  return (
    <StyledNav
      className={classNames("c-nav", className)}
      $align={alignment}
      $role={role}
      ref={wrapRef}
    >
      <StyledNavList ref={navRef}>
        {type === "inline" &&
          Children.toArray(children).map((child: ReactNode, index: number) => {
            const element = child as ReactElement;
            return cloneElement(element, {
              active: index === active,
              role,
              onClick,
              "data-index": index,
              "data-disabled": element.props.disabled,
            });
          })}
        {type === "dropdown" ? (
          <StyledNavListItemMore name={navItemNames[active]} role={role}>
            <Select
              selected={active}
              target={navAsSelect}
              disabledInternalSort
              options={allOptions}
              type="single"
              mode="normal"
              forceCloseMenu
              disableSearch
              keepSameOptionsOrder
              onChange={onMoreOptionClick}
              onSelectOpen={setMoreOpen}
              onSelectClose={setMoreClose}
            />
          </StyledNavListItemMore>
        ) : null}
      </StyledNavList>
    </StyledNav>
  );
};

Nav.displayName = "Nav";
